import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import GradientHouseIcon from "./GradientHouseIcon";
import VendorleadsGradientText from "./VendorleadsGradientText";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";
const GRAY_COLOR = "#f3f4f6";
const GRAY_DARK_COLOR = "#374151";
const WHITE_COLOR = "#fff";
const BORDER_COLOR_LIGHT = "#eee";

const FEATURE_LINKS = [
  { to: "/features/monthly-ai-property-report", label: "AI Property Updates", icon: "\uD83D\uDCCA", sub: "Automated monthly property insights" },
  { to: "/features/diy-ai-property-valuator", label: "DIY AI Valuator", icon: "\uD83C\uDFE0", sub: "Self-service property valuations" },
  { to: "/features/property-appraisals", label: "AI Appraisals", icon: "\uD83C\uDFE1", sub: "Premium property appraisal presentations" },
  { to: "/features/ai-news-articles", label: "AI News Articles", icon: "\uD83D\uDCF0", sub: "AI-written real estate content" },
  { to: "/features/ai-co-pilot", label: "AI Co-Pilot", icon: "\uD83E\uDD16", sub: "Intelligent lead nurturing" },
  { to: "/features/qr-code-flyer", label: "QR Code Campaigns", icon: "\uD83D\uDCF1", sub: "Print-to-lead generation" },
  { to: "/features/sms-campaigns", label: "SMS Campaigns", icon: "💬", sub: "Two-way AI text messaging" },
  { to: "/features/map-prospector", label: "Map Prospector", icon: "📍", sub: "Visualise contact activity on a map" },
];

export { FEATURE_LINKS };

/**
 * Navbar - A responsive, sticky navigation bar component for the Vendorleads application.
 *
 * Renders a top navigation bar with logo, feature links (via dropdown on desktop,
 * inline list on mobile), and a Sign In/Dashboard button.
 * Automatically adapts between desktop and mobile layouts at a 768px breakpoint.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.onSignIn - Callback invoked when the "Sign In" button is clicked.
 *
 * ## Padding Values Explained
 *
 * Padding values throughout the component use CSS shorthand notation in `rem` units
 * (1rem = 16px by default):
 *
 * - **`"0.75rem 0"`** (`nav`): 12px top/bottom, 0 left/right — vertical breathing room for the navbar.
 * - **`"0 1rem"`** (inner container): 0 top/bottom, 16px left/right — horizontal gutters within the navbar.
 * - **`"0.25rem 0"`** (Features button): 4px top/bottom, 0 left/right — minimal vertical tap area around text.
 * - **`"0.5rem"`** (dropdown panel padding): 8px on all four sides — inner spacing around the dropdown card.
 * - **`"0.7rem 0.85rem"`** (desktop dropdown links): 11.2px top/bottom, 13.6px left/right — comfortable click targets in the dropdown.
 * - **`"0.7rem 1.5rem"`** (desktop CTA buttons): 11.2px top/bottom, 24px left/right — prominent, wide button hit area.
 * - **`"0.5rem 1rem"`** (mobile menu container): 8px top/bottom, 16px left/right — matches the navbar's horizontal gutters.
 * - **`"0.65rem 0.75rem"`** (mobile menu links): 10.4px top/bottom, 12px left/right — touch-friendly tap targets on mobile.
 * - **`"0.75rem 1rem"`** (mobile CTA button): 12px top/bottom, 16px left/right — large, full-width touch target.
 * - **`"0.25rem"`** (hamburger button): 4px on all sides — small padding to enlarge the tap area around the icon.
 *
 * When **two values** are provided (e.g., `"0.75rem 0"`), the first is vertical (top/bottom)
 * and the second is horizontal (left/right). When **one value** is provided (e.g., `"0.5rem"`),
 * it applies equally to all four sides.
 *
 * @example
 * <Navbar onSignIn={() => openAuthModal()} />
 *
 * @returns {React.ReactElement} A sticky navigation bar element.
 */
export default function Navbar({ onSignIn }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const featuresRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isMobile && mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobile, mobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("jwt_token");
    setIsAuthenticated(!!token);
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (featuresRef.current && !featuresRef.current.contains(e.target)) {
        setFeaturesOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav
      style={{
        background: WHITE_COLOR,
        borderBottom: `1px solid ${BORDER_COLOR_LIGHT}`,
        padding: "0.75rem 0",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 1rem",
        }}
      >
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: "0.5rem", textDecoration: "none" }}>
          <GradientHouseIcon width="30" height="30" />
          <VendorleadsGradientText style={{ fontSize: isMobile ? "1.25rem" : "1.75rem" }} />
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
              <div
                ref={featuresRef}
                style={{ position: "relative" }}
                onMouseEnter={() => setFeaturesOpen(true)}
                onMouseLeave={() => setFeaturesOpen(false)}
              >
                <button
                  style={{
                    color: featuresOpen ? BRAND_COLOR : GRAY_DARK_COLOR,
                    background: featuresOpen ? GRAY_COLOR : "none",
                    border: "none",
                    fontWeight: 500,
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    cursor: "pointer",
                    padding: "0.35rem 0.7rem",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                    transition: "color 0.15s, background 0.15s",
                  }}
                >
                  Features
                  <span
                    style={{
                      fontSize: "0.6rem",
                      marginTop: "2px",
                      transition: "transform 0.2s",
                      transform: featuresOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  >
                    {"\u25BC"}
                  </span>
                </button>
                {featuresOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      paddingTop: "0.5rem",
                    }}
                  >
                    <div
                      style={{
                        background: WHITE_COLOR,
                        borderRadius: "12px",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)",
                        padding: "0.5rem",
                        zIndex: 2000,
                        border: `1px solid ${BORDER_COLOR_LIGHT}`,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        minWidth: "540px",
                      }}
                    >
                      {FEATURE_LINKS.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            padding: "0.7rem 0.85rem",
                            color: GRAY_DARK_COLOR,
                            textDecoration: "none",
                            borderRadius: "8px",
                            transition: "background 0.15s",
                          }}
                          onMouseOver={(e) => (e.currentTarget.style.background = GRAY_COLOR)}
                          onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
                          onClick={() => setFeaturesOpen(false)}
                        >
                          <span style={{ fontSize: "1.2rem", width: "28px", textAlign: "center", flexShrink: 0 }}>{item.icon}</span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "0.92rem", lineHeight: 1.3, color: GRAY_DARK_COLOR }}>{item.label}</div>
                            <div style={{ fontSize: "0.78rem", color: "#94a3b8", marginTop: "0.1rem", lineHeight: 1.3 }}>{item.sub}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Link
                to="/pricing"
                style={{
                  color: GRAY_DARK_COLOR,
                  textDecoration: "none",
                  fontWeight: 500,
                  transition: "color 0.15s, background 0.15s",
                  padding: "0.35rem 0.7rem",
                  borderRadius: "6px",
                }}
                onMouseOver={(e) => { e.currentTarget.style.color = BRAND_COLOR; e.currentTarget.style.background = GRAY_COLOR; }}
                onMouseOut={(e) => { e.currentTarget.style.color = GRAY_DARK_COLOR; e.currentTarget.style.background = "transparent"; }}
              >
                Pricing
              </Link>
              <Link
                to="/testimonials"
                style={{
                  color: GRAY_DARK_COLOR,
                  textDecoration: "none",
                  fontWeight: 500,
                  transition: "color 0.15s, background 0.15s",
                  padding: "0.35rem 0.7rem",
                  borderRadius: "6px",
                }}
                onMouseOver={(e) => { e.currentTarget.style.color = BRAND_COLOR; e.currentTarget.style.background = GRAY_COLOR; }}
                onMouseOut={(e) => { e.currentTarget.style.color = GRAY_DARK_COLOR; e.currentTarget.style.background = "transparent"; }}
              >
                Testimonials
              </Link>
              <Link
                to="/blog"
                style={{
                  color: GRAY_DARK_COLOR,
                  textDecoration: "none",
                  fontWeight: 500,
                  transition: "color 0.15s, background 0.15s",
                  padding: "0.35rem 0.7rem",
                  borderRadius: "6px",
                }}
                onMouseOver={(e) => { e.currentTarget.style.color = BRAND_COLOR; e.currentTarget.style.background = GRAY_COLOR; }}
                onMouseOut={(e) => { e.currentTarget.style.color = GRAY_DARK_COLOR; e.currentTarget.style.background = "transparent"; }}
              >
                Blog
              </Link>
              <Link
                to="/changelog"
                style={{
                  color: GRAY_DARK_COLOR,
                  textDecoration: "none",
                  fontWeight: 500,
                  transition: "color 0.15s, background 0.15s",
                  padding: "0.35rem 0.7rem",
                  borderRadius: "6px",
                }}
                onMouseOver={(e) => { e.currentTarget.style.color = BRAND_COLOR; e.currentTarget.style.background = GRAY_COLOR; }}
                onMouseOut={(e) => { e.currentTarget.style.color = GRAY_DARK_COLOR; e.currentTarget.style.background = "transparent"; }}
              >
                Changelog
              </Link>
            </div>
          )}
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              {isAuthenticated ? (
                <button
                  style={{
                    border: "none",
                    borderRadius: "8px",
                    padding: "0.7rem 1.5rem",
                    fontSize: "1rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    background: `linear-gradient(90deg, ${BRAND_COLOR}, ${BRAND_ACCENT_COLOR})`,
                    color: WHITE_COLOR,
                    transition: "transform 0.15s, box-shadow 0.15s",
                    boxShadow: "0 2px 8px rgba(16,185,129,0.2)",
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.35)"; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(16,185,129,0.2)"; }}
                  onClick={() => navigate("/dashboard")}
                >
                  Dashboard
                </button>
              ) : (
                <button
                  style={{
                    border: "none",
                    borderRadius: "8px",
                    padding: "0.7rem 1.5rem",
                    fontSize: "1rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    background: `linear-gradient(90deg, ${BRAND_COLOR}, ${BRAND_ACCENT_COLOR})`,
                    color: WHITE_COLOR,
                    transition: "transform 0.15s, box-shadow 0.15s",
                    boxShadow: "0 2px 8px rgba(16,185,129,0.2)",
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.35)"; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(16,185,129,0.2)"; }}
                  onClick={() => onSignIn()}
                >
                  Sign In
                </button>
              )}
            </div>
          )}
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "1.5rem",
                color: GRAY_DARK_COLOR,
                padding: "0.25rem",
                display: "flex",
                alignItems: "center",
              }}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? "\u2715" : "\u2630"}
            </button>
          )}
        </div>
      </div>
      {isMobile && mobileMenuOpen && (
        <div
          style={{
            position: "fixed",
            top: "57px",
            left: 0,
            right: 0,
            bottom: 0,
            background: WHITE_COLOR,
            zIndex: 999,
            display: "flex",
            flexDirection: "column",
            borderTop: `1px solid ${BORDER_COLOR_LIGHT}`,
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              padding: "0.5rem 1rem 0.5rem 1rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.15rem",
            }}
          >
          {FEATURE_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "0.65rem 0.75rem 0.65rem 0",
                color: GRAY_DARK_COLOR,
                textDecoration: "none",
                fontSize: "0.95rem",
                fontWeight: 500,
                borderRadius: "8px",
                transition: "background 0.15s",
              }}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span style={{ fontSize: "1.1rem", width: "24px", textAlign: "center", flexShrink: 0 }}>{item.icon}</span>
              {item.label}
            </Link>
          ))}
          <div style={{ borderTop: `1px solid ${BORDER_COLOR_LIGHT}`, margin: "0.4rem 0" }} />
          <Link
            to="/pricing"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.65rem 0.75rem 0.65rem 0",
              color: GRAY_DARK_COLOR,
              textDecoration: "none",
              fontSize: "0.95rem",
              fontWeight: 500,
              borderRadius: "8px",
              transition: "background 0.15s",
            }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <span style={{ fontSize: "1.1rem", width: "24px", textAlign: "center", flexShrink: 0 }}>{"\uD83D\uDCB0"}</span>
            Pricing
          </Link>
          <Link
            to="/testimonials"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.65rem 0.75rem 0.65rem 0",
              color: GRAY_DARK_COLOR,
              textDecoration: "none",
              fontSize: "0.95rem",
              fontWeight: 500,
              borderRadius: "8px",
              transition: "background 0.15s",
            }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <span style={{ fontSize: "1.1rem", width: "24px", textAlign: "center", flexShrink: 0 }}>{"\u2B50"}</span>
            Testimonials
          </Link>
          <div style={{ borderTop: `1px solid ${BORDER_COLOR_LIGHT}`, margin: "0.4rem 0" }} />
          <Link
            to="/blog"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.65rem 0.75rem 0.65rem 0",
              color: GRAY_DARK_COLOR,
              textDecoration: "none",
              fontSize: "0.95rem",
              fontWeight: 500,
              borderRadius: "8px",
              transition: "background 0.15s",
            }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <span style={{ fontSize: "1.1rem", width: "24px", textAlign: "center", flexShrink: 0 }}>{"\u270D\uFE0F"}</span>
            Blog
          </Link>
          <Link
            to="/changelog"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.65rem 0.75rem 0.65rem 0",
              color: GRAY_DARK_COLOR,
              textDecoration: "none",
              fontSize: "0.95rem",
              fontWeight: 500,
              borderRadius: "8px",
              transition: "background 0.15s",
            }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <span style={{ fontSize: "1.1rem", width: "24px", textAlign: "center", flexShrink: 0 }}>{"\uD83D\uDCCB"}</span>
            Changelog
          </Link>
          </div>
          <div
            style={{
              flexShrink: 0,
              padding: "0.75rem 1rem 1rem 1rem",
              borderTop: `1px solid ${BORDER_COLOR_LIGHT}`,
              background: WHITE_COLOR,
            }}
          >
          {isAuthenticated ? (
            <button
              style={{
                border: "none",
                borderRadius: "8px",
                padding: "0.75rem 1rem",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
                background: `linear-gradient(90deg, ${BRAND_COLOR}, ${BRAND_ACCENT_COLOR})`,
                color: WHITE_COLOR,
                width: "100%",
                textAlign: "center",
              }}
              onClick={() => {
                setMobileMenuOpen(false);
                navigate("/dashboard");
              }}
            >
              Dashboard
            </button>
          ) : (
            <button
              style={{
                border: "none",
                borderRadius: "8px",
                padding: "0.75rem 1rem",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
                background: `linear-gradient(90deg, ${BRAND_COLOR}, ${BRAND_ACCENT_COLOR})`,
                color: WHITE_COLOR,
                width: "100%",
                textAlign: "center",
              }}
              onClick={() => {
                setMobileMenuOpen(false);
                onSignIn();
              }}
            >
              Sign In
            </button>
          )}
          </div>
        </div>
      )}
    </nav>
  );
}
